import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

interface RelatedPost {
  slug: string;
  title: string;
  excerpt: string | null;
}

interface Props {
  posts: RelatedPost[];
}

const FALLBACK_LINKS = [
  { to: "/agencies", label: "For agencies", desc: "Run your temp desk on Youtemp." },
  { to: "/employers", label: "For employers", desc: "Book and manage temp staff." },
  { to: "/temps", label: "For temps", desc: "Find shifts that fit your life." },
  { to: "/waitlist/", label: "Join the waitlist", desc: "Be first in line at launch." },
];

export function BlogRelated({ posts }: Props) {
  const hasPosts = posts.length > 0;
  return (
    <section className="mt-16 pt-10 border-t border-border">
      <h2 className="text-xl md:text-2xl font-heading font-semibold mb-6">
        Explore more
      </h2>
      {hasPosts ? (
        <ul
          className={
            // Mobile: horizontal CSS scroll-snap carousel.
            // sm+: regular 2/3-column grid.
            "flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4 pb-2 " +
            "sm:mx-0 sm:px-0 sm:pb-0 sm:overflow-visible sm:snap-none sm:grid sm:grid-cols-2 lg:grid-cols-3"
          }
        >
          {posts.map((p) => (
            <li
              key={p.slug}
              className="snap-start shrink-0 min-w-[78vw] max-w-[20rem] sm:min-w-0 sm:max-w-none"
            >
              <Link
                to={`/blog/${p.slug}/`}
                className="block h-full rounded-lg border border-border bg-card p-5 hover:border-primary/50 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <h3 className="text-base font-semibold leading-snug mb-2">
                  {p.title}
                </h3>
                {p.excerpt && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{p.excerpt}</p>
                )}
                <span className="mt-3 inline-flex items-center text-sm text-primary">
                  Read article <ArrowRight className="ml-1 h-3.5 w-3.5" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {FALLBACK_LINKS.map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                className="block rounded-lg border border-border bg-card p-5 hover:border-primary/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <p className="font-semibold mb-1">{l.label}</p>
                <p className="text-sm text-muted-foreground">{l.desc}</p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}